import { Modal, ModalContent, ModalBody, Spinner } from "@heroui/react";
import { Phone, PhoneCall, PhoneDisconnect, PhoneX, User, Keyboard } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useEffect, useState } from "react";

type CallStatus = "idle" | "connecting" | "ringing" | "connected" | "disconnected";

interface CallStatusModalProps {
  isOpen: boolean;
  status: CallStatus;
  phoneNumber: string;
  contactName?: string;
  onHangup: () => void;
  onClose: () => void;
  onSendDigit?: (digit: string) => void;
}

const DIAL_KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

// DTMF frequency pairs per digit (row freq + column freq)
const DTMF_FREQUENCIES: Record<string, [number, number]> = {
  "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
  "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
  "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
  "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
};

const playDtmfTone = (digit: string, durationMs = 150) => {
  const freqs = DTMF_FREQUENCIES[digit];
  if (!freqs) return;

  try {
    const ctx = new AudioContext();
    const [f1, f2] = freqs;
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    gainNode.connect(ctx.destination);

    [f1, f2].forEach((freq) => {
      const osc = ctx.createOscillator();
      osc.frequency.value = freq;
      osc.connect(gainNode);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + durationMs / 1000);
    });

    // Clean up after tone finishes
    setTimeout(() => ctx.close(), durationMs + 50);
  } catch {
    // Audio not available, silently skip
  }
};

/**
 * Modal that shows call status, duration, and allows hanging up
 */
export const CallStatusModal = ({
  isOpen,
  status,
  phoneNumber,
  contactName,
  onHangup,
  onClose,
  onSendDigit,
}: CallStatusModalProps) => {
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [dialedDigits, setDialedDigits] = useState("");

  // Track call duration when connected
  useEffect(() => {
    if (status === "connected" && !startTime) {
      setStartTime(Date.now());
    }

    if (status === "connected") {
      const interval = setInterval(() => {
        if (startTime) {
          setDuration(Math.floor((Date.now() - startTime) / 1000));
        }
      }, 1000);
      return () => clearInterval(interval);
    }

    // Reset when call ends
    if (status === "disconnected" || status === "idle") {
      setStartTime(null);
      setDuration(0);
      setShowKeypad(false);
      setDialedDigits("");
    }
  }, [status, startTime]);

  const handleDialKey = (digit: string) => {
    playDtmfTone(digit);
    onSendDigit?.(digit);
    setDialedDigits((prev) => (prev + digit).slice(-8));
  };

  // Format duration as MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Format phone number for display
  const formatPhone = (phone: string) => {
    return phone.replace(/(\+32)(\d{3})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
  };

  // Get status display info
  const getStatusInfo = () => {
    switch (status) {
      case "connecting":
        return {
          label: "Verbinden...",
          icon: <Spinner size="sm" color="warning" />,
          color: "text-amber-500",
          bgColor: "bg-amber-50",
        };
      case "ringing":
        return {
          label: "Gaat over...",
          icon: <PhoneCall size={24} className="animate-bounce text-amber-500" />,
          color: "text-amber-500",
          bgColor: "bg-amber-50",
        };
      case "connected":
        return {
          label: formatDuration(duration),
          icon: <Phone size={24} className="text-green-500" />,
          color: "text-green-500",
          bgColor: "bg-green-50",
        };
      case "disconnected":
        return {
          label: "Verbinding verbroken",
          icon: <PhoneX size={24} className="text-neutral-400" />,
          color: "text-neutral-500",
          bgColor: "bg-neutral-50",
        };
      default:
        return {
          label: "Inactief",
          icon: <Phone size={24} className="text-neutral-400" />,
          color: "text-neutral-500",
          bgColor: "bg-neutral-50",
        };
    }
  };

  const statusInfo = getStatusInfo();
  const canHangup = status === "connecting" || status === "ringing" || status === "connected";

  return (
    <Modal
      isOpen={isOpen}
      onClose={status === "disconnected" || status === "idle" ? onClose : undefined}
      hideCloseButton={canHangup}
      isDismissable={!canHangup}
      size="sm"
    >
      <ModalContent>
        <ModalBody className="py-8">
          <div className="flex flex-col items-center gap-4">
            {/* Avatar / Icon */}
            <div className={`p-6 rounded-full ${statusInfo.bgColor}`}>
              {status === "connecting" ? (
                <Spinner size="lg" color="warning" />
              ) : status === "ringing" ? (
                <PhoneCall size={48} weight="duotone" className="text-amber-500 animate-pulse" />
              ) : status === "connected" ? (
                <Phone size={48} weight="duotone" className="text-green-500" />
              ) : (
                <User size={48} weight="duotone" className="text-neutral-400" />
              )}
            </div>

            {/* Contact Info */}
            <div className="text-center">
              {contactName && (
                <h3 className="text-lg font-semibold text-neutral-primary">
                  {contactName}
                </h3>
              )}
              <p className="text-neutral-secondary-mid">
                {formatPhone(phoneNumber)}
              </p>
            </div>

            {/* Status */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusInfo.bgColor}`}>
              {statusInfo.icon}
              <span className={`font-medium ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
            </div>

            {/* DTMF Keypad (only when connected) */}
            {status === "connected" && showKeypad && (
              <div className="w-full">
                {/* Dialed digits display */}
                <div className="h-8 mb-2 px-3 flex items-center justify-end rounded bg-neutral-100 font-mono text-sm text-neutral-primary tracking-widest min-w-0">
                  {dialedDigits || <span className="text-neutral-400 font-sans tracking-normal text-xs">Ingedrukte toetsen</span>}
                </div>
                {/* Key grid */}
                <div className="grid grid-cols-3 gap-2">
                  {DIAL_KEYS.flat().map((key) => (
                    <button
                      key={key}
                      onClick={() => handleDialKey(key)}
                      className="h-12 rounded-lg bg-neutral-100 hover:bg-moss-primary/10 active:bg-moss-primary/20 text-neutral-primary font-semibold text-lg transition-colors"
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-4">
              {canHangup ? (
                <>
                  {status === "connected" && (
                    <button
                      onClick={() => setShowKeypad((v) => !v)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        showKeypad
                          ? "bg-moss-primary/10 text-moss-primary"
                          : "bg-neutral-100 text-neutral-secondary-mid hover:bg-neutral-200"
                      }`}
                    >
                      <Keyboard size={16} />
                      Toetsen
                    </button>
                  )}
                  <ButtonNew
                    variant="primaryStatusRed"
                    icon={<PhoneDisconnect size={20} />}
                    label="Ophangen"
                    onClick={onHangup}
                  />
                </>
              ) : (
                <ButtonNew
                  variant="secondaryNeutral"
                  label="Sluiten"
                  onClick={onClose}
                />
              )}
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CallStatusModal;
